/**
 * Bongio Form Router — Shared client-side logic
 *
 * Provides:
 * - Country/Region dropdown population and conditional display
 * - AJAX form submission to form-router.php
 * - Success/error UI feedback
 *
 * Usage from Liquid:
 *   <script src="{{ 'form-router.js' | asset_url }}" defer></script>
 *   Then call BongioFormRouter.init(formEl, type) from an inline script or other JS.
 */

(function () {
  'use strict';

  const ENDPOINT = 'https://pdf.bongio.com/form-router.php';
  const TOKEN = 'BONGIO_PDF_SECRET_2026';

  // ── Meta CAPI proxy (server-side Lead event) ──
  // Deploy URL canonico: bongio-instagram-proxy.vercel.app (Vercel project name
  // storico — il progetto Vercel non è stato rinominato).
  // Repo GitHub: bongio-meta-proxy (rinominata dopo il pivot Instagram→CAPI).
  // Codice live: /Users/paoloferraris/Projects/bongio/meta-proxy/api/meta-lead.js
  // L'endpoint deduplica con il client-side fbq('track','Lead') via event_id.
  const META_CAPI_ENDPOINT = 'https://bongio-instagram-proxy.vercel.app/api/meta-lead';
  // Shared secret (anti-spam soft). Stesso valore di CAPI_PROXY_SECRET su Vercel.
  // Esposto nel JS bundle come il TOKEN sopra — non è bullet-proof, alza solo
  // la barriera contro chiamate random/bot al proxy.
  const META_CAPI_PROXY_SECRET = '97219233115b8949af6db1610e3a73a635dbbc8ae932aafd142fad25b5fe0ae5';

  // Detect current language from the <html lang="..."> attribute
  function detectLang() {
    const htmlLang = (document.documentElement.lang || 'it').toLowerCase().split('-')[0];
    const valid = ['it', 'en', 'de', 'fr', 'es', 'ru', 'ar'];
    return valid.includes(htmlLang) ? htmlLang : 'en';
  }

  // ── Country & Region Data ─────────────────────────────────────────────────

  const COUNTRIES_PRIORITY = [
    { code: 'IT', label: { it:'Italia', en:'Italy', de:'Italien', fr:'Italie', es:'Italia', ru:'Италия', ar:'إيطاليا' }},
    { code: 'FR', label: { it:'Francia', en:'France', de:'Frankreich', fr:'France', es:'Francia', ru:'Франция', ar:'فرنسا' }},
    { code: 'DE', label: { it:'Germania', en:'Germany', de:'Deutschland', fr:'Allemagne', es:'Alemania', ru:'Германия', ar:'ألمانيا' }},
    { code: 'ES', label: { it:'Spagna', en:'Spain', de:'Spanien', fr:'Espagne', es:'España', ru:'Испания', ar:'إسبانيا' }},
    { code: 'GB', label: { it:'Regno Unito', en:'United Kingdom', de:'Vereinigtes Königreich', fr:'Royaume-Uni', es:'Reino Unido', ru:'Великобритания', ar:'المملكة المتحدة' }},
    { code: 'US', label: { it:'Stati Uniti', en:'United States', de:'Vereinigte Staaten', fr:'États-Unis', es:'Estados Unidos', ru:'США', ar:'الولايات المتحدة' }},
    { code: 'CH', label: { it:'Svizzera', en:'Switzerland', de:'Schweiz', fr:'Suisse', es:'Suiza', ru:'Швейцария', ar:'سويسرا' }},
    { code: 'AT', label: { it:'Austria', en:'Austria', de:'Österreich', fr:'Autriche', es:'Austria', ru:'Австрия', ar:'النمسا' }},
    { code: 'NL', label: { it:'Paesi Bassi', en:'Netherlands', de:'Niederlande', fr:'Pays-Bas', es:'Países Bajos', ru:'Нидерланды', ar:'هولندا' }},
    { code: 'PT', label: { it:'Portogallo', en:'Portugal', de:'Portugal', fr:'Portugal', es:'Portugal', ru:'Португалия', ar:'البرتغال' }},
    { code: 'GR', label: { it:'Grecia', en:'Greece', de:'Griechenland', fr:'Grèce', es:'Grecia', ru:'Греция', ar:'اليونان' }},
    { code: 'RU', label: { it:'Russia', en:'Russia', de:'Russland', fr:'Russie', es:'Rusia', ru:'Россия', ar:'روسيا' }},
    { code: 'UA', label: { it:'Ucraina', en:'Ukraine', de:'Ukraine', fr:'Ukraine', es:'Ucrania', ru:'Украина', ar:'أوكرانيا' }},
    { code: 'AE', label: { it:'Emirati Arabi', en:'United Arab Emirates', de:'Vereinigte Arabische Emirate', fr:'Émirats arabes unis', es:'Emiratos Árabes', ru:'ОАЭ', ar:'الإمارات' }},
    { code: 'SA', label: { it:'Arabia Saudita', en:'Saudi Arabia', de:'Saudi-Arabien', fr:'Arabie saoudite', es:'Arabia Saudita', ru:'Саудовская Аравия', ar:'السعودية' }},
    { code: 'SI', label: { it:'Slovenia', en:'Slovenia', de:'Slowenien', fr:'Slovénie', es:'Eslovenia', ru:'Словения', ar:'سلوفينيا' }},
    { code: 'HR', label: { it:'Croazia', en:'Croatia', de:'Kroatien', fr:'Croatie', es:'Croacia', ru:'Хорватия', ar:'كرواتيا' }},
    { code: 'RS', label: { it:'Serbia', en:'Serbia', de:'Serbien', fr:'Serbie', es:'Serbia', ru:'Сербия', ar:'صربيا' }},
    { code: 'RO', label: { it:'Romania', en:'Romania', de:'Rumänien', fr:'Roumanie', es:'Rumania', ru:'Румыния', ar:'رومانيا' }},
    { code: 'BG', label: { it:'Bulgaria', en:'Bulgaria', de:'Bulgarien', fr:'Bulgarie', es:'Bulgaria', ru:'Болгария', ar:'بلغاريا' }},
    { code: 'AL', label: { it:'Albania', en:'Albania', de:'Albanien', fr:'Albanie', es:'Albania', ru:'Албания', ar:'ألبانيا' }},
    { code: 'BA', label: { it:'Bosnia-Erzegovina', en:'Bosnia and Herzegovina', de:'Bosnien und Herzegowina', fr:'Bosnie-Herzégovine', es:'Bosnia-Herzegovina', ru:'Босния и Герцеговина', ar:'البوسنة والهرسك' }},
    { code: 'ME', label: { it:'Montenegro', en:'Montenegro', de:'Montenegro', fr:'Monténégro', es:'Montenegro', ru:'Черногория', ar:'الجبل الأسود' }},
    { code: 'MK', label: { it:'Macedonia del Nord', en:'North Macedonia', de:'Nordmazedonien', fr:'Macédoine du Nord', es:'Macedonia del Norte', ru:'Северная Македония', ar:'مقدونيا الشمالية' }},
    { code: 'XK', label: { it:'Kosovo', en:'Kosovo', de:'Kosovo', fr:'Kosovo', es:'Kosovo', ru:'Косово', ar:'كوسوفو' }},
    { code: 'BE', label: { it:'Belgio', en:'Belgium', de:'Belgien', fr:'Belgique', es:'Bélgica', ru:'Бельгия', ar:'بلجيكا' }},
    { code: 'PL', label: { it:'Polonia', en:'Poland', de:'Polen', fr:'Pologne', es:'Polonia', ru:'Польша', ar:'بولندا' }},
    { code: 'SE', label: { it:'Svezia', en:'Sweden', de:'Schweden', fr:'Suède', es:'Suecia', ru:'Швеция', ar:'السويد' }},
    { code: 'DK', label: { it:'Danimarca', en:'Denmark', de:'Dänemark', fr:'Danemark', es:'Dinamarca', ru:'Дания', ar:'الدنمارك' }},
    { code: 'NO', label: { it:'Norvegia', en:'Norway', de:'Norwegen', fr:'Norvège', es:'Noruega', ru:'Норвегия', ar:'النرويج' }},
    { code: 'FI', label: { it:'Finlandia', en:'Finland', de:'Finnland', fr:'Finlande', es:'Finlandia', ru:'Финляндия', ar:'فنلندا' }},
    { code: 'IE', label: { it:'Irlanda', en:'Ireland', de:'Irland', fr:'Irlande', es:'Irlanda', ru:'Ирландия', ar:'أيرلندا' }},
    { code: 'CZ', label: { it:'Rep. Ceca', en:'Czech Republic', de:'Tschechien', fr:'Rép. tchèque', es:'Rep. Checa', ru:'Чехия', ar:'التشيك' }},
    { code: 'HU', label: { it:'Ungheria', en:'Hungary', de:'Ungarn', fr:'Hongrie', es:'Hungría', ru:'Венгрия', ar:'المجر' }},
    { code: 'SK', label: { it:'Slovacchia', en:'Slovakia', de:'Slowakei', fr:'Slovaquie', es:'Eslovaquia', ru:'Словакия', ar:'سلوفاكيا' }},
    { code: 'CN', label: { it:'Cina', en:'China', de:'China', fr:'Chine', es:'China', ru:'Китай', ar:'الصين' }},
    { code: 'JP', label: { it:'Giappone', en:'Japan', de:'Japan', fr:'Japon', es:'Japón', ru:'Япония', ar:'اليابان' }},
    { code: 'IN', label: { it:'India', en:'India', de:'Indien', fr:'Inde', es:'India', ru:'Индия', ar:'الهند' }},
    { code: 'BR', label: { it:'Brasile', en:'Brazil', de:'Brasilien', fr:'Brésil', es:'Brasil', ru:'Бразилия', ar:'البرازيل' }},
    { code: 'AU', label: { it:'Australia', en:'Australia', de:'Australien', fr:'Australie', es:'Australia', ru:'Австралия', ar:'أستراليا' }},
    { code: 'CA', label: { it:'Canada', en:'Canada', de:'Kanada', fr:'Canada', es:'Canadá', ru:'Канада', ar:'كندا' }},
    { code: 'MX', label: { it:'Messico', en:'Mexico', de:'Mexiko', fr:'Mexique', es:'México', ru:'Мексика', ar:'المكسيك' }},
    { code: 'TR', label: { it:'Turchia', en:'Turkey', de:'Türkei', fr:'Turquie', es:'Turquía', ru:'Турция', ar:'تركيا' }},
    { code: 'IL', label: { it:'Israele', en:'Israel', de:'Israel', fr:'Israël', es:'Israel', ru:'Израиль', ar:'إسرائيل' }},
    { code: 'KR', label: { it:'Corea del Sud', en:'South Korea', de:'Südkorea', fr:'Corée du Sud', es:'Corea del Sur', ru:'Южная Корея', ar:'كوريا الجنوبية' }},
    { code: 'ZZ', label: { it:'Altro', en:'Other', de:'Andere', fr:'Autre', es:'Otro', ru:'Другое', ar:'أخرى' }},
  ];

  const REGIONS_IT = [
    'Abruzzo', 'Basilicata', 'Calabria', 'Campania', 'Emilia-Romagna',
    'Friuli-Venezia Giulia', 'Lazio', 'Liguria', 'Lombardia', 'Marche',
    'Molise', 'Piemonte', 'Puglia', 'Sardegna', 'Sicilia',
    'Toscana', 'Trentino-Alto Adige', 'Umbria', "Valle d'Aosta", 'Veneto'
  ];

  const LABELS = {
    it: { country: 'Paese', region: 'Regione', select: 'Seleziona...', sending: 'Invio in corso...', error: 'Si è verificato un errore. Riprova.',
          success_title: 'Richiesta inviata', success_text: 'Ti risponderemo al più presto. Grazie per averci contattato.',
          help_type: 'Tipo di richiesta',
          help_type_options: [
            { value: 'commerciale', label: 'Informazioni commerciali' },
            { value: 'tecnica',     label: 'Assistenza tecnica' },
            { value: 'stampa',      label: 'Stampa / Press' },
            { value: 'altro',       label: 'Altro' },
          ]},
    en: { country: 'Country', region: 'Region', select: 'Select...', sending: 'Sending...', error: 'An error occurred. Please try again.',
          success_title: 'Request sent', success_text: "We'll get back to you shortly. Thank you for contacting us.",
          help_type: 'Type of request',
          help_type_options: [
            { value: 'commerciale', label: 'Commercial information' },
            { value: 'tecnica',     label: 'Technical support' },
            { value: 'stampa',      label: 'Press' },
            { value: 'altro',       label: 'Other' },
          ]},
    de: { country: 'Land', region: 'Region', select: 'Auswählen...', sending: 'Wird gesendet...', error: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
          success_title: 'Anfrage gesendet', success_text: 'Wir melden uns in Kürze bei Ihnen. Vielen Dank für Ihre Kontaktaufnahme.',
          help_type: 'Art der Anfrage',
          help_type_options: [
            { value: 'commerciale', label: 'Kommerzielle Informationen' },
            { value: 'tecnica',     label: 'Technischer Support' },
            { value: 'stampa',      label: 'Presse' },
            { value: 'altro',       label: 'Sonstiges' },
          ]},
    fr: { country: 'Pays', region: 'Région', select: 'Sélectionner...', sending: 'Envoi en cours...', error: 'Une erreur est survenue. Veuillez réessayer.',
          success_title: 'Demande envoyée', success_text: 'Nous vous répondrons dans les plus brefs délais. Merci de nous avoir contactés.',
          help_type: 'Type de demande',
          help_type_options: [
            { value: 'commerciale', label: 'Informations commerciales' },
            { value: 'tecnica',     label: 'Support technique' },
            { value: 'stampa',      label: 'Presse' },
            { value: 'altro',       label: 'Autre' },
          ]},
    es: { country: 'País', region: 'Región', select: 'Seleccionar...', sending: 'Enviando...', error: 'Se ha producido un error. Inténtelo de nuevo.',
          success_title: 'Solicitud enviada', success_text: 'Le responderemos a la brevedad. Gracias por contactarnos.',
          help_type: 'Tipo de solicitud',
          help_type_options: [
            { value: 'commerciale', label: 'Información comercial' },
            { value: 'tecnica',     label: 'Soporte técnico' },
            { value: 'stampa',      label: 'Prensa' },
            { value: 'altro',       label: 'Otro' },
          ]},
    ru: { country: 'Страна', region: 'Регион', select: 'Выбрать...', sending: 'Отправка...', error: 'Произошла ошибка. Пожалуйста, попробуйте снова.',
          success_title: 'Запрос отправлен', success_text: 'Мы свяжемся с вами в ближайшее время. Благодарим вас за обращение.',
          help_type: 'Тип запроса',
          help_type_options: [
            { value: 'commerciale', label: 'Коммерческая информация' },
            { value: 'tecnica',     label: 'Техническая поддержка' },
            { value: 'stampa',      label: 'Пресса' },
            { value: 'altro',       label: 'Другое' },
          ]},
    ar: { country: 'البلد', region: 'المنطقة', select: '...اختر', sending: '...جارٍ الإرسال', error: 'حدث خطأ. يرجى المحاولة مرة أخرى.',
          success_title: 'تم إرسال الطلب', success_text: 'سنرد عليك في أقرب وقت. شكراً لتواصلك معنا.',
          help_type: 'نوع الطلب',
          help_type_options: [
            { value: 'commerciale', label: 'معلومات تجارية' },
            { value: 'tecnica',     label: 'دعم فني' },
            { value: 'stampa',      label: 'صحافة' },
            { value: 'altro',       label: 'أخرى' },
          ]},
  };

  // ── Country/Region dropdown injection ─────────────────────────────────────
  // Markup minimal-underline: <div.fr-field> > <label.fr-label> + <select.fr-input>

  function createCountryRegionFields(container, lang, detectedCountry) {
    const l = LABELS[lang] || LABELS.en;
    const uid = 'fr-' + Math.random().toString(36).slice(2, 8);

    // ── Country ──
    const countryField = document.createElement('div');
    countryField.className = 'fr-field';
    countryField.innerHTML = `
      <label class="fr-label" for="${uid}-country">${l.country}<span class="fr-label__req" aria-hidden="true">*</span></label>
      <select id="${uid}-country" name="country" class="fr-input" required>
        <option value="">${l.select}</option>
      </select>`;
    const countrySelect = countryField.querySelector('select');

    COUNTRIES_PRIORITY.forEach(c => {
      const opt = document.createElement('option');
      opt.value = c.code;
      opt.textContent = c.label[lang] || c.label.en;
      if (c.code === detectedCountry) opt.selected = true;
      countrySelect.appendChild(opt);
    });

    // ── Region (shown only when country === IT) ──
    const regionField = document.createElement('div');
    regionField.className = 'fr-field form-router__region-group';
    regionField.style.display = 'none';
    regionField.innerHTML = `
      <label class="fr-label" for="${uid}-region">${l.region}<span class="fr-label__req" aria-hidden="true">*</span></label>
      <select id="${uid}-region" name="region" class="fr-input">
        <option value="">${l.select}</option>
      </select>`;
    const regionSelect = regionField.querySelector('select');

    REGIONS_IT.forEach(r => {
      const opt = document.createElement('option');
      opt.value = r;
      opt.textContent = r;
      regionSelect.appendChild(opt);
    });

    function toggleRegion() {
      const isItaly = countrySelect.value === 'IT';
      regionField.style.display = isItaly ? '' : 'none';
      regionSelect.required = isItaly;
      if (!isItaly) regionSelect.value = '';
    }

    countrySelect.addEventListener('change', toggleRegion);
    container.appendChild(countryField);
    container.appendChild(regionField);
    toggleRegion();
  }

  // ── Help type dropdown (contact form) ─────────────────────────────────────
  // Options: commerciale / tecnica / stampa / altro (localized)

  function createHelpTypeField(container, lang, opts) {
    const options = opts || {};
    const required = options.required !== false;
    const name = options.name || 'help_type';
    const l = LABELS[lang] || LABELS.en;
    const uid = 'fr-help-' + Math.random().toString(36).slice(2, 8);

    const field = document.createElement('div');
    field.className = 'fr-field';
    const reqMark = required
      ? '<span class="fr-label__req" aria-hidden="true">*</span>'
      : '';
    field.innerHTML = `
      <label class="fr-label" for="${uid}">${l.help_type}${reqMark}</label>
      <select id="${uid}" name="${name}" class="fr-input"${required ? ' required' : ''}>
        <option value="">${l.select}</option>
      </select>`;
    const sel = field.querySelector('select');
    (l.help_type_options || []).forEach(o => {
      const opt = document.createElement('option');
      opt.value = o.value;
      opt.textContent = o.label;
      sel.appendChild(opt);
    });

    container.appendChild(field);
    return sel;
  }

  // ── Meta Pixel helpers ────────────────────────────────────────────────────
  // Read a cookie by name. Used to capture _fbp / _fbc for CAPI server-side
  // deduplication (FIX 2). Safe in any environment — returns null if missing.

  function readCookie(name) {
    try {
      const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1') + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : null;
    } catch { return null; }
  }

  // Generate an event_id so the client `fbq('track','Lead')` and the
  // server-side CAPI Lead event (sent by pdf-server/form-router.php) can be
  // deduplicated by Meta. Same id must travel on both legs.
  function generateEventId() {
    return 'lead_' + Date.now() + '_' + Math.random().toString(36).slice(2, 10);
  }

  // ── AJAX submission ───────────────────────────────────────────────────────

  async function submitForm(formEl, type) {
    const lang = detectLang();
    const l = LABELS[lang] || LABELS.en;
    const submitBtn = formEl.querySelector('[type="submit"]');
    const originalBtnText = submitBtn ? submitBtn.innerHTML : '';

    // Collect all named fields
    const formData = new FormData(formEl);
    const payload = { type, lang, token: TOKEN };

    for (const [key, value] of formData.entries()) {
      if (key === 'privacy') continue;
      const cleanKey = key.replace(/^contact\[/, '').replace(/\]$/, '');
      payload[cleanKey] = value;
    }

    // ── Meta tracking metadata (FIX 1 + groundwork for FIX 2 CAPI) ──
    // We attach: event_id (for client↔server dedup), _fbp/_fbc cookies
    // (Meta-set first-party IDs), source_url and user_agent.
    // The server-side CAPI call in pdf-server/form-router.php will consume
    // these to send a parallel Lead event server-side.
    const metaEventId = generateEventId();
    payload._meta_event_id = metaEventId;
    payload._fbp = readCookie('_fbp');
    payload._fbc = readCookie('_fbc');
    payload.source_url = window.location.href;
    payload.user_agent = navigator.userAgent;

    // Show loading state
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.innerHTML = `<span>${l.sending}</span>`;
    }

    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (result.success) {
        // ── Meta Pixel: fire client-side Lead event ──
        // Wrapped in try/catch + typeof guard so a missing/blocked pixel
        // never breaks the submit flow. eventID matches the server CAPI
        // event so Meta deduplica le due trasmissioni.
        //
        // I form B2B (`type='b2b'`) NON sparano il Lead pixel: sono richieste
        // di agente B2B, non lead utente. Il proxy CAPI fa lo stesso skip.
        // Vedi meta-proxy/api/meta-lead.js.
        try {
          if (typeof window.fbq === 'function' && type !== 'b2b') {
            const fbqProps = {
              content_name: type,             // 'product' | 'contact' | 'hub' | 'cart'
              content_category: 'lead_form',
            };
            // Bongio non espone prezzi agli utenti finali (solo B2B), quindi
            // NON mandiamo `value`. Per i cart lead mandiamo `num_items` come
            // proxy di intent (più prodotti = più committment). Stesso campo
            // che mandiamo al CAPI proxy. Vedi meta-proxy/api/meta-lead.js.
            if (type === 'cart' && payload.cart_item_count) {
              const n = Number(payload.cart_item_count);
              if (Number.isFinite(n) && n > 0) {
                fbqProps.num_items = Math.floor(n);
              }
            }
            window.fbq('track', 'Lead', fbqProps, { eventID: metaEventId });
          }
        } catch (err) {
          if (window.console && console.warn) console.warn('[form-router] fbq Lead failed:', err);
        }

        // ── Meta CAPI server-side Lead event (fire-and-forget) ──
        // Inoltra al proxy Vercel che hasha le PII e chiama Meta CAPI.
        // NON aspettiamo la risposta: il successo del submit utente non
        // dipende dal CAPI. Errori vengono solo loggati.
        try {
          const capiPayload = { ...payload, proxy_secret: META_CAPI_PROXY_SECRET };
          fetch(META_CAPI_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(capiPayload),
            // keepalive consente alla richiesta di completarsi anche se la
            // pagina cambia subito dopo (es. redirect post-submit).
            keepalive: true,
          }).catch((err) => {
            if (window.console && console.warn) console.warn('[form-router] CAPI proxy failed:', err);
          });
        } catch (err) {
          if (window.console && console.warn) console.warn('[form-router] CAPI proxy threw:', err);
        }

        showSuccess(formEl);
        return true;
      } else {
        showError(formEl, result.error || l.error);
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.innerHTML = originalBtnText;
        }
        return false;
      }
    } catch {
      showError(formEl, l.error);
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.innerHTML = originalBtnText;
      }
      return false;
    }
  }

  // ── UI feedback ───────────────────────────────────────────────────────────

  function showSuccess(formEl) {
    const lang = detectLang();
    const l = LABELS[lang] || LABELS.en;

    const html = `
      <div class="form-router__success-icon" aria-hidden="true">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      </div>
      <h3 class="form-router__success-title">${l.success_title}</h3>
      <p class="form-router__success-text">${l.success_text}</p>
      <div class="form-router__success-sign">Io, Bongio.</div>`;

    let successEl = formEl.querySelector('.form-router__success');
    if (successEl) {
      // Hide fields container (various possible names across the 4 forms)
      const fieldsContainer = formEl.querySelector('.form-router__fields, .contact-form__fields, .request-info-modal__form-fields, .b2b-panel__form-fields');
      if (fieldsContainer) {
        fieldsContainer.style.display = 'none';
      } else {
        // Hide all direct children of the form except the success block
        Array.from(formEl.children).forEach(child => {
          if (child !== successEl) child.style.display = 'none';
        });
      }
      successEl.innerHTML = html;
      successEl.classList.add('is-visible');
      successEl.style.display = '';
      // focus for a11y
      if (typeof successEl.focus === 'function') {
        try { successEl.focus({ preventScroll: false }); } catch { successEl.focus(); }
      }
    } else {
      const wrapper = document.createElement('div');
      wrapper.className = 'form-router__success is-visible';
      wrapper.tabIndex = -1;
      wrapper.innerHTML = html;
      formEl.innerHTML = '';
      formEl.appendChild(wrapper);
      try { wrapper.focus({ preventScroll: false }); } catch { wrapper.focus(); }
    }
  }

  function showError(formEl, msg) {
    let errEl = formEl.querySelector('.form-router__error');
    if (!errEl) {
      errEl = document.createElement('div');
      errEl.className = 'form-router__error';
      errEl.style.cssText = 'color:#e74c3c;font-size:14px;margin-bottom:16px;text-align:center;';
      const firstField = formEl.querySelector('.fr-field, .form-router__field-group, .field, .b2b-panel__form-group, .request-info-modal__form-group');
      if (firstField) formEl.insertBefore(errEl, firstField);
      else formEl.prepend(errEl);
    }
    errEl.textContent = msg;
    errEl.style.display = '';
    setTimeout(() => { errEl.style.display = 'none'; }, 6000);
  }

  // ── Public API ────────────────────────────────────────────────────────────

  window.BongioFormRouter = {
    detectLang,
    createCountryRegionFields,
    createHelpTypeField,
    submitForm,
    showSuccess,
    showError,
    ENDPOINT,
    TOKEN,
    COUNTRIES_PRIORITY,
    REGIONS_IT,
    LABELS,
  };

})();
